// The second gate of a plan's words (docs/architecture/plans-and-docs.md, §5;
// today's plans/html.tsx): the sanitiser's nodes (model.ts, `Content`) drawn
// as React elements through an allowlist of their own, with every issue key
// in their text linked to its issue and, where the mode says, its state:
// `chip` adds the state's icon and name, `icon` the icon alone, `plain`
// neither. A key inside a link or code stays as written. Keys become links
// from text nodes alone, after both gates. Nothing here parses a string as
// HTML: the nodes are read, and React makes its own.
import { createContext, createElement, Fragment, useContext, useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Issue } from '../api/types'
import { StateIcon } from '../components/Icons'
import { keysIn } from '../lib/keys'
import { stateName } from '../vocab'
import type { Content } from './model'
import { DROPPED, ELEMENTS, planClasses, safeHref, safeImage, USER_CONTENT_PREFIX } from './sanitize'
import { resolverOf, type Resolve } from './state'

export type KeyMode = 'chip' | 'icon' | 'plain'

export interface PlanKeys {
  resolve: Resolve
  /** The key names keys are read in: every project's of the workspace. */
  names: readonly string[]
}

const NO_KEYS: PlanKeys = { resolve: () => undefined, names: [] }
const KeysContext = createContext<PlanKeys>(NO_KEYS)
export const PlanKeysProvider = KeysContext.Provider

/** How keys are read and looked up: by the workspace's projects' keys, in the store's issues. */
export function usePlanKeys(issues: ReadonlyMap<string, Issue>, names: readonly string[]): PlanKeys {
  const signature = names.join('\u0000')
  return useMemo(() => ({ resolve: resolverOf(issues), names: signature ? signature.split('\u0000') : [] }), [issues, signature])
}

/** An issue's page. */
export function issuePath(issue: Pick<Issue, 'key'>): string {
  return `/${encodeURIComponent(issue.key)}`
}

/** A key: its link, then what `mode` adds of its issue's state. */
export function IssueKey({ issueKey, mode }: { issueKey: string; mode: KeyMode }) {
  const { resolve } = useContext(KeysContext)
  const issue = resolve(issueKey)
  const link = (
    <Link className="sun-ref" to={issue ? issuePath(issue) : `/${encodeURIComponent(issueKey)}`}>
      {issue?.key ?? issueKey}
    </Link>
  )
  if (!issue || mode === 'plain') return link
  if (mode === 'icon') {
    return (
      <>
        {link}
        <span className="pv-keyicon">
          <StateIcon state={issue.state} size={12} labelled />
        </span>
      </>
    )
  }
  return (
    <>
      {link}
      <span className="pv-keystate">
        <StateIcon state={issue.state} size={12} />
        {stateName(issue.state)}
      </span>
    </>
  )
}

// ---------------------------------------------------------------- the conversion

const DRAWN = new Set(ELEMENTS)
const GONE = new Set(DROPPED)
const VOID = new Set(['br', 'hr', 'img'])
/** Where the parser's white space is no child React accepts. */
const NO_TEXT = new Set(['table', 'thead', 'tbody', 'tr'])
const RENAMED: Readonly<Record<string, string>> = { class: 'className', colspan: 'colSpan', rowspan: 'rowSpan', datetime: 'dateTime' }
/** The attributes drawn, `aria-*` besides; the sanitiser kept no other, and this is a second gate. */
const KEPT = new Set(['class', 'data-met', 'data-on', 'data-state', 'href', 'title', 'alt', 'src', 'colspan', 'rowspan', 'datetime', 'open', 'lang', 'dir', 'id'])
const SPAN = /^[1-9][0-9]{0,2}$/
const DIRECTION = new Set(['ltr', 'rtl', 'auto'])
/** A link that leaves the app opens beside it, never in its place, and tells nothing of where it was followed from. */
const ELSEWHERE = /^(?:https?:|mailto:)/i

function propsOf(element: Element, key: number): Record<string, unknown> {
  const tag = element.localName
  const props: Record<string, unknown> = { key }
  for (const attr of Array.from(element.attributes)) {
    const name = attr.name.toLowerCase()
    const value = attr.value
    if (name.startsWith('aria-')) {
      if (/^aria-[a-z]+$/.test(name)) props[name] = value
      continue
    }
    if (!KEPT.has(name)) continue
    switch (name) {
      case 'class': {
        const kept = planClasses(value)
        if (kept) props.className = kept
        continue
      }
      case 'href':
        if (tag !== 'a' || !safeHref(value)) continue
        break
      case 'src':
        if (tag !== 'img' || !safeImage(value)) continue
        break
      case 'id':
        // Only DOMPurify's prefixed ids: a plan names none of the app's.
        if (!value.startsWith(USER_CONTENT_PREFIX)) continue
        break
      case 'colspan':
      case 'rowspan':
        if ((tag !== 'td' && tag !== 'th') || !SPAN.test(value)) continue
        break
      case 'dir':
        if (!DIRECTION.has(value.toLowerCase())) continue
        break
      case 'open':
        if (tag === 'details') props.open = true
        continue
    }
    props[RENAMED[name] ?? name] = value
  }
  if (tag === 'a' && typeof props.href === 'string' && ELSEWHERE.test(props.href.trim())) {
    props.target = '_blank'
    props.rel = 'noopener noreferrer'
  }
  if (tag === 'img') {
    props.loading = 'lazy'
    props.decoding = 'async'
    props.referrerPolicy = 'no-referrer'
  }
  return props
}

function textNodes(text: string, names: readonly string[], mode: KeyMode, key: number): ReactNode {
  const found = keysIn(text, names)
  if (found.length === 0) return text
  const out: ReactNode[] = []
  let at = 0
  for (const match of found) {
    if (match.index > at) out.push(text.slice(at, match.index))
    out.push(<IssueKey key={`${key}:${match.index}`} issueKey={match.key} mode={mode} />)
    at = match.index + match.length
  }
  if (at < text.length) out.push(text.slice(at))
  return <Fragment key={key}>{out}</Fragment>
}

/** How deep the conversion draws elements; below, an element is drawn as its text, so a body nested past the parser's bound never recurses without end. */
const MAX_DEPTH = 64

/** How many elements one conversion draws; past it, what is left is drawn as its text. */
const MAX_ELEMENTS = 20_000

function convert(nodes: ArrayLike<Node>, names: readonly string[], mode: KeyMode, literal: boolean, parent: string, depth = 0, budget = { left: MAX_ELEMENTS }): ReactNode[] {
  const out: ReactNode[] = []
  Array.from(nodes).forEach((node, index) => {
    if (node.nodeType === 3) {
      const text = node.textContent ?? ''
      if (NO_TEXT.has(parent) && text.trim() === '') return
      out.push(literal ? text : textNodes(text, names, mode, index))
      return
    }
    if (node.nodeType !== 1) return
    const element = node as Element
    const tag = element.localName
    // What runs, styles, frames, draws or loads goes with all it holds, as at the first gate.
    if (GONE.has(tag)) return
    if (depth >= MAX_DEPTH || budget.left-- <= 0) {
      out.push(element.textContent ?? '')
      return
    }
    const inner = literal || tag === 'a' || tag === 'code' || tag === 'pre'
    if (!DRAWN.has(tag)) {
      // The sanitiser keeps none of these; were one here, only its words would be drawn.
      out.push(<Fragment key={index}>{element.textContent ?? ''}</Fragment>)
      return
    }
    const props = propsOf(element, index)
    if (tag === 'a' && typeof props.href === 'string' && props.href.startsWith('/') && !props.target) {
      // A path in the app goes through the router, as the app's own links do.
      const { href, ...rest } = props
      out.push(createElement(Link, { ...rest, to: href } as never, ...convert(element.childNodes, names, mode, true, tag, depth + 1, budget)))
      return
    }
    out.push(VOID.has(tag) ? createElement(tag, props) : createElement(tag, props, ...convert(element.childNodes, names, mode, inner, tag, depth + 1, budget)))
  })
  return out
}

/** Sanitised nodes as React elements, keys linked as `mode` says. */
export function contentNodes(content: Content, names: readonly string[], mode: KeyMode): ReactNode[] {
  if (content.length === 0) return []
  return convert(content, names, mode, false, '')
}

export interface PlanHtmlProps {
  content: Content
  mode: KeyMode
  /** The element that holds it; a fragment when none. */
  as?: 'span' | 'div' | 'p'
  className?: string
}

export function PlanHtml({ content, mode, as, className }: PlanHtmlProps) {
  const { names } = useContext(KeysContext)
  // The names' signature stands for them: a board that changes an issue's state keeps its key names.
  const signature = names.join('\u0000')
  const nodes = useMemo(() => contentNodes(content, signature ? signature.split('\u0000') : [], mode), [content, signature, mode])
  return as ? createElement(as, { className }, ...nodes) : <>{nodes}</>
}
